import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { enumToOptions } from "services/helper";
import { Column, TableAction } from "types/globalTypes";
import { PlanDto, GroupType, LicenseTier } from "types/assessmentTypes";
import {
  useCreatePlanMutation,
  useDeletePlanMutation,
  useGetLicensePlansQuery,
  useUpdatePlanMutation,
} from "reduxFiles/api/licenseApi";

const AdminLicensePlans = () => {
  // hooks
  const { t } = useTranslation();

  // states
  const [item, setItem] = useState<PlanDto | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetLicensePlansQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters: filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreatePlanMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdatePlanMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeletePlanMutation();

  // form and table
  const fields: Field[] = [
    {
      name: "title",
      label: t("licenses.title"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "price",
      label: t("licenses.price"),
      type: "number",
      formed: true,
      required: true,
    },
    {
      name: "search",
      label: t("licenses.title"),
      type: "text",
      filtered: true,
    },
    {
      name: "groupType",
      label: t("licenses.groupType"),
      type: "select",
      options: enumToOptions(GroupType, t, "group.types"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "tier",
      label: t("licenses.tier"),
      type: "select",
      options: enumToOptions(LicenseTier, t, "licenses.tiers"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "isActive",
      label: t("licenses.isActive"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      formed: true,
      required: true,
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<PlanDto>[] = [
    {
      key: "title",
      title: t("licenses.title"),
    },
    {
      key: "price",
      title: t("licenses.price"),
      render: (row) =>
        `${row.price.toLocaleString()} ${t("actions.priceInit")}`,
    },
    {
      key: "tier",
      title: t("licenses.tier"),
      render: (row) => t(`licenses.tiers.${row.tier}`),
    },
    {
      key: "groupType",
      title: t("licenses.groupType"),
      render: (row) => t(`group.types.${row.groupType}`),
    },
    {
      key: "isActive",
      title: t("licenses.isActive"),
      render: (row) => t(`common.${row.isActive ? "yes" : "no"}`),
    },
  ];

  const actions: TableAction<PlanDto>[] = [
    {
      label: t("licenses.updateLicensePlan"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("licenses.deleteLicensePlan"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      if (item) {
        await updateApi({ id: item.id, data: formData }).unwrap();
      } else {
        await addApi(formData).unwrap();
      }
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("licenses.licensePlanList")}
          createBtn={{
            title: t("licenses.addLicensePlan"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<PlanDto>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={item ? { ...item, isActive: `${item.isActive}` } : {}}
          title={
            item
              ? t("licenses.updateLicensePlan")
              : t("licenses.addLicensePlan")
          }
          isEdit={!!item}
          loading={item ? isUpdating : isAdding}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("licenses.deleteLicensePlan")}
          desc={t("actions.confirmDeleteDesc", { name: item?.title || "" })}
        />
      </div>
    </div>
  );
};

export default AdminLicensePlans;
